import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BlockBuilder } from 'src/v3/components/show-notes-editor'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { BackIcon, SaveIcon } from 'src/v3/core/icons'
import {
  fetchShowNotesLibrary,
  selectShowNotesLibrary,
  selectShowNotesLibraryStatus,
} from '../create.slice'
import { updateShowNotes } from '../utils/api'
import { useAutoSaveMeta } from '../hooks/use-auto-save-meta'
import { CREATE_SHOW_NOTES_URLS } from '../constants'

type LocationState = {
  backLabel?: string
  backPath?: string
}

export default function ShowNotesEditPage(): ReactElement {
  let { id } = useParams<{ id: string }>()
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()

  let state = location.state as LocationState | null
  let backLabel = state?.backLabel ?? 'Back to Show Notes Library'
  let backPath = state?.backPath ?? CREATE_SHOW_NOTES_URLS.LIBRARY

  let showNotesLibrary = useAppSelector(selectShowNotesLibrary)
  let showNotesLibraryStatus = useAppSelector(selectShowNotesLibraryStatus)
  useAutoFetch(showNotesLibraryStatus, fetchShowNotesLibrary)

  let showNotes = showNotesLibrary.find((sn) => sn.id === id)

  let { showError, NotificationComponent } = useNotifications()

  let [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: { retry: 1 },
        },
      }),
  )

  let initialFields = useMemo(
    () => ({
      name: showNotes?.name ?? '',
      description: showNotes?.description ?? '',
    }),
    [showNotes?.name, showNotes?.description],
  )

  let {
    fields,
    isSaving: isMetaSaving,
    hasChanges,
    handleFieldChange,
    handleManualSave,
  } = useAutoSaveMeta({
    id,
    initialFields,
    saveFn: (saveID, { name, description }) =>
      updateShowNotes(saveID, { name, description }),
    onSaveSuccess: () => dispatch(fetchShowNotesLibrary()),
    onError: (msg) => showError(msg),
  })

  let handleBack = () => navigate(backPath)

  let isLoading = showNotesLibraryStatus === 'pending' && !showNotes

  if (isLoading) {
    return (
      <Box
        className="flex items-center justify-center py-24"
        role="status"
        aria-label="Loading show notes"
      >
        <CircularProgress aria-hidden="true" />
      </Box>
    )
  }

  if (!showNotes && showNotesLibraryStatus === 'fulfilled') {
    return (
      <Container maxWidth="xl" className="py-6">
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          aria-label={backLabel}
        >
          {backLabel}
        </Button>
        <Typography color="text.secondary" className="mt-6 text-center">
          Show notes not found.
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center justify-between mb-6">
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          aria-label={backLabel}
        >
          {backLabel}
        </Button>
        <Button
          variant="contained"
          startIcon={
            isMetaSaving ? (
              <CircularProgress size={14} color="inherit" aria-hidden="true" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={!hasChanges || isMetaSaving || !fields.name.trim()}
          onClick={handleManualSave}
          aria-busy={isMetaSaving}
        >
          {isMetaSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
        </Button>
      </Box>
      <TextField
        fullWidth
        variant="standard"
        value={fields.name}
        onChange={(e) => handleFieldChange('name', e.target.value)}
        placeholder="Name"
        slotProps={{
          input: {
            disableUnderline: true,
            inputProps: { 'aria-label': 'Show notes name' },
            sx: {
              typography: 'h4',
              '& input': { padding: 0 },
            },
          },
        }}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        variant="standard"
        value={fields.description}
        onChange={(e) => handleFieldChange('description', e.target.value)}
        placeholder="Description..."
        slotProps={{
          input: {
            disableUnderline: true,
            inputProps: { 'aria-label': 'Show notes description' },
            sx: {
              fontSize: '1.1rem',
              fontStyle: 'italic',
              color: 'text.secondary',
              '& textarea': { padding: 0 },
            },
          },
        }}
        sx={{ mb: 4 }}
      />
      {id && (
        <QueryClientProvider client={queryClient}>
          <BlockBuilder
            showNotesID={id}
            basePath="v2/create/show-notes"
            showNotesName={fields.name}
            showNotesDescription={fields.description}
            showAutoSaveStatus
          />
        </QueryClientProvider>
      )}
      {NotificationComponent}
    </Container>
  )
}
